<script lang="ts">
import { defineComponent, onMounted, provide, ref, computed, h, defineAsyncComponent } from 'vue'
import { usePaymentForm } from '~/shared/use-payment-form'
import {
  normalizeSensePassWidgetFormIds,
  resolveClientRuntime,
  resolveCreditCardWidgetUrl,
  resolveSensePassWidgetVersionFromQuery
} from '~/shared/runtime-environment'
import { useRoute } from 'vue-router'
import { useRuntimeConfig } from 'nuxt/app'
import LogRocket from 'logrocket'
import {
  findTemplateModuleKey,
  resolveThankYouTemplateId,
  thankYouTemplateUsesMessage
} from '~/shared/thank-you-template'
import {
  applyPledgeToPaymentForm,
  fetchPledge,
  type Pledge
} from '~/shared/pledge-form'

export default defineComponent({
  name: 'PledgeFormContainer',
  props: {
    id: { type: String, default: undefined },
    testMode: { type: Boolean, default: false },
    callback: { type: String, default: undefined },
  },
  setup(props) {
    const route = useRoute()
    const runtimeConfig = useRuntimeConfig()
    const sensepassWidgetFormIds = normalizeSensePassWidgetFormIds(
      runtimeConfig.public.sensepassWidgetFormIds
    )
    const sensepassWidgetVersion = resolveSensePassWidgetVersionFromQuery(
      route.query as Record<string, unknown>
    )
    const runtime = resolveClientRuntime({
      locationOrigin: typeof window !== 'undefined' ? window.location.origin : undefined,
      explicitTestMode: props.testMode
    })
    const testMode = runtime.testMode

    if (typeof window !== 'undefined') {
      //@ts-ignore
      window.isProduction = runtime.isProduction

      const nuxt = ((window as any).__NUXT__ = (window as any).__NUXT__ || {})
      nuxt.config = nuxt.config || { public: {} }
      nuxt.config.public = nuxt.config.public || {}
      nuxt.config.public.testMode = testMode
      nuxt.config.public.api = runtime.apiUrl
      nuxt.config.public.apiUrl = runtime.apiUrl
      ;(window as any).__PAYMENT_FORM_API_URL__ = runtime.apiUrl
    }

    onMounted(() => {
      // Load GTM with runtime-resolved container ID
      ;(function(w: any, d: Document, s: string, l: string, i: string) {
        w[l] = w[l] || []
        w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
        const f = d.getElementsByTagName(s)[0]
        const j = d.createElement(s) as HTMLScriptElement
        const dl = l !== 'dataLayer' ? '&l=' + l : ''
        j.async = true
        j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl
        f.parentNode?.insertBefore(j, f)
      })(window, document, 'script', 'dataLayer', runtime.gtmId)

      //@ts-ignore
      if (window.isProduction === true)
        LogRocket.init('orthodox-union/donation-forms')

      fetch('https://payment.ouapis.org/api/payment/gateways')
      setInterval(() => {
        fetch('https://payment.ouapis.org/api/payment/gateways')
      }, 4000 * 60)

      const script = document.createElement('script')
      script.defer = true
      script.type = 'module'
      script.src = resolveCreditCardWidgetUrl(
        testMode,
        props.id,
        sensepassWidgetFormIds,
        sensepassWidgetVersion
      )
      document.body.appendChild(script)
    })

    const paymentForm = usePaymentForm(props.id, { testMode })

    if (!paymentForm) {
      throw new Error('PaymentForm context not available')
    }

    const { submitted, settings, request, setValue, actorRef } = paymentForm

    const pledge = ref<Pledge>()
    const params = route.query as Record<string, string>

    onMounted(() => {
      if (!actorRef) return
      const subscription = actorRef.subscribe((snapshot: any) => {
        if (snapshot.matches('editing')) {
          fetchPledge(params)
            .then(result => {
              pledge.value = applyPledgeToPaymentForm(
                result,
                params,
                setValue as any,
                settings
              )
            })
            .catch(error => {
              console.error('Pledge form could not load pledge data', error)
            })

          subscription.unsubscribe()
        }
      })
    })

    provide('pledge', pledge)

    // Resolve pledge/payment form templates using import.meta.glob
    const formModules = import.meta.glob(['../pledge-form-templates/**/*.vue', '../payment-form-templates/**/*.vue'])
    const thankYouTemplateModules = import.meta.glob('../thank-you-template/**/*.vue')

    const pledgeFormComponent = computed(() => {
      if (!props.id) return null
      const templateId = settings?.template || props.id

      const matchingPath = Object.keys(formModules).find(path => {
        const filename = path.split('/').pop()?.replace('.vue', '')
        return filename === templateId
      })

      if (matchingPath) {
        return defineAsyncComponent(formModules[matchingPath] as any)
      }
      return null
    })

    const isSubmitted = computed(() => {
      return route.query.thankyou !== undefined || submitted.value
    })

    const thankYouTemplateId = computed(() => {
      return resolveThankYouTemplateId(
        props.id,
        settings,
        Object.keys(thankYouTemplateModules)
      )
    })

    const thankYouComponent = computed(() => {
      const moduleKey = findTemplateModuleKey(
        thankYouTemplateModules,
        thankYouTemplateId.value
      )
      if (!moduleKey) return null
      return defineAsyncComponent(thankYouTemplateModules[moduleKey] as any)
    })

    return () => {
      if (isSubmitted.value) {
        return h('div', {}, [
          thankYouComponent.value
            ? h(
              thankYouComponent.value,
              thankYouTemplateUsesMessage(thankYouTemplateId.value)
                ? { message: settings?.message }
                : {}
            )
            : null
        ])
      }

      return h('div', {}, [
        pledgeFormComponent.value ? h(pledgeFormComponent.value) : null
      ])
    }
  }
})
</script>
